import type { FormConfig, PropertySchema } from '@takeshape/schema';
import { parseRef } from 'app/schema-form/util/refs';
import { fromJS, List } from 'immutable';
import { type ComponentType, useMemo } from 'react';
import type { ImmutableMap } from '../../../common/utils/immutable-types.js';
import type { ResolveRef, SchemaFieldProps } from '../../types.js';
import { getDiscriminatorConstant, getDiscriminatorName } from '../../util/discriminator.js';
import { getOneOfOptionTitle, getWidgetTitle } from '../../util/widget.js';
import SchemaField from '../schema-field.jsx';
import DiscriminatorField from './discriminator-field.js';
import { ObjectFieldWrapper } from './object-field-wrapper.js';

const nope = () => false;
const emptyMap = fromJS({}) as ImmutableMap<FormConfig>;

// TODO use resolved schema from hook instead of doing this here
function getPropIsRequiredOneOf(oneOf: List<ImmutableMap<PropertySchema>>, resolveRef: ResolveRef, typeName?: string) {
  const one = oneOf.find((value) => {
    const { typeName: oneTypeName } = parseRef(value!.get('$ref'));
    return oneTypeName === typeName;
  });
  const oneSchema = one && resolveRef(one);
  const required = oneSchema ? oneSchema.get('required') : List();
  return (name: string) => (required ? required.includes(name) : false);
}

function getPropIsRequired(required?: List<string>) {
  if (!required) {
    return nope;
  }

  const requiredSet = required.toSet();
  return (name: string): boolean => requiredSet.has(name);
}

type GetNamesProps = {
  oneOf: boolean;
} & Pick<ObjectFieldProps, 'typeName' | 'forms' | 'config'> &
  Pick<Required<ObjectFieldProps>, 'properties'>;

function getNames({ typeName, forms, config, oneOf, properties }: GetNamesProps): string[] {
  let names;
  // For oneOf use order in default form config
  if (oneOf) {
    names = forms?.getIn([typeName, 'default', 'order']);
  }

  // use inline order
  names ||= config.get('order');

  // fall back on order in default form config
  if (!names && typeName) {
    names = forms?.getIn([typeName, 'default', 'order']);
  }

  // fall back on natural order
  names ||= properties.keySeq();

  return names.toArray();
}

type GetPropertyConfigProps = {
  name: string;
  oneOf: boolean;
} & Pick<ObjectFieldProps, 'typeName' | 'forms' | 'config'>;

function getPropertyConfig({ typeName, oneOf, forms, config, name }: GetPropertyConfigProps): ImmutableMap<FormConfig> {
  let propertyConfig = emptyMap;
  // For oneOf use default form config
  if (oneOf) {
    propertyConfig = forms?.getIn([typeName, 'default', 'properties', name], emptyMap) ?? emptyMap;
  }

  // use inline form config
  if (propertyConfig === emptyMap) {
    propertyConfig = config.getIn(['properties', name], emptyMap);
  }

  // fall back on default form config
  if (propertyConfig === emptyMap && typeName) {
    propertyConfig = forms?.getIn([typeName, 'default', 'properties', name], emptyMap) ?? emptyMap;
  }

  return propertyConfig;
}

type GetWrapperContext = Pick<ObjectFieldProps, 'typeName' | 'forms' | 'config' | 'widgets'>;

function getWrapper(context: GetWrapperContext, name: string): ComponentType<SchemaFieldProps> | undefined {
  const { typeName, forms, config, widgets } = context;
  const wrapperName = config.get(name) ?? forms?.getIn([typeName, 'default', name]);
  if (wrapperName && widgets?.[wrapperName]) {
    return widgets[wrapperName] as ComponentType<SchemaFieldProps>;
  }
}

type GetObjectWrapperProps = Pick<
  ObjectFieldProps,
  'typeName' | 'forms' | 'config' | 'schema' | 'widgets' | 'isArrayItem'
>;

function getObjectWrapper(context: GetObjectWrapperProps): ComponentType<SchemaFieldProps> | undefined {
  const wrapper = getWrapper(context, 'wrapper');
  if (wrapper) {
    return wrapper;
  }

  // Do not show object wrapper for array items or objects without an explicit title set
  const title = !context.isArrayItem && getWidgetTitle({ schema: context.schema, config: context.config });
  if (title) {
    return ObjectFieldWrapper as ComponentType<SchemaFieldProps>;
  }
}

function getPropertiesWrapper(context: GetWrapperContext): ComponentType<SchemaFieldProps> | undefined {
  return getWrapper(context, 'propertiesWrapper');
}

export type ObjectFieldHookProps = {
  readonly resolvedSchemaPath: string[];
  readonly properties?: ImmutableMap<Record<string, PropertySchema>>;
  readonly typeName?: string;
  readonly isArrayItem: boolean;
};

export type ObjectFieldProps = SchemaFieldProps & ObjectFieldHookProps;

export default function ObjectField(props: ObjectFieldProps) {
  const {
    resolveRef,
    schema,
    schemaPath,
    resolvedSchemaPath,
    formName,
    path,
    config,
    context,
    widgets,
    disabled,
    readOnly,
    locale,
    forms,
    typeName,
    properties,
    isArrayItem
  } = props;

  const oneOf = schema.get('oneOf');
  const discriminatorName = getDiscriminatorName(schema);

  const discriminatorSchema = useMemo((): ImmutableMap<PropertySchema> | undefined => {
    if (oneOf && discriminatorName) {
      return fromJS({
        title: getWidgetTitle({ schema, path, config }),
        oneOf: oneOf.map((oneOrUndefined) => {
          const one = oneOrUndefined!;
          const schema = resolveRef(one);
          const title = getOneOfOptionTitle({ schema, forms }, one.get('$ref'));
          const value = getDiscriminatorConstant(schema, discriminatorName);
          return fromJS({ title, enum: [value] });
        })
      });
    }
  }, [oneOf, discriminatorName, schema, path, forms, config, resolveRef]);

  const discriminatorConfig = useMemo(
    (): ImmutableMap<FormConfig> =>
      discriminatorName
        ? fromJS({
            widget: 'dropdown',
            testId: `${formName}-${path}.${discriminatorName}`
          }).merge(config.getIn(['properties', discriminatorName], emptyMap))
        : emptyMap,

    [discriminatorName, formName, path, config]
  );

  const moreThanOneOf = oneOf && oneOf.size > 1;

  const isPropRequired = useMemo(
    () => (oneOf ? getPropIsRequiredOneOf(oneOf, resolveRef, typeName) : getPropIsRequired(schema.get('required'))),
    [oneOf, resolveRef, schema, typeName]
  );

  const names = useMemo(
    () =>
      properties
        ? getNames({
            typeName,
            properties,
            oneOf: Boolean(oneOf),
            forms,
            config
          })
        : [],
    [config, forms, oneOf, properties, typeName]
  );

  if (!names.length && !moreThanOneOf) {
    if (widgets?.emptyObject) {
      const EmptyObject = widgets.emptyObject as ComponentType<SchemaFieldProps>;
      return <EmptyObject {...props} />;
    }
  }

  let discriminatorField: JSX.Element | null = null;
  const showDiscriminator =
    oneOf && oneOf.size > 1 && (!isArrayItem || (discriminatorName && names.includes(discriminatorName)));
  if (showDiscriminator && discriminatorName && discriminatorSchema) {
    discriminatorField = (
      <DiscriminatorField
        key={discriminatorName}
        resolveRef={resolveRef}
        schema={discriminatorSchema}
        schemaPath={schemaPath}
        formName={formName}
        path={path ? `${path}.${discriminatorName}` : discriminatorName}
        isRequired
        config={discriminatorConfig}
        context={context}
        widgets={widgets}
        disabled={disabled}
        readOnly={readOnly}
        locale={locale}
        forms={forms}
      />
    );
  }

  const fields = properties
    ? names.map((name) => {
        const propertySchema = properties.get(name);
        if (!propertySchema || (name === discriminatorName && discriminatorField)) {
          return null;
        }

        const propertyConfig = getPropertyConfig({
          typeName,
          oneOf: Boolean(oneOf),
          forms,
          config,
          name
        });
        const propertySchemaPath = [...resolvedSchemaPath, 'properties', name];

        const field = (
          <SchemaField
            resolveRef={resolveRef}
            key={name}
            schema={propertySchema}
            schemaPath={propertySchemaPath}
            formName={formName}
            path={path ? `${path}.${name}` : name}
            isRequired={isPropRequired(name)}
            config={propertyConfig}
            context={context}
            widgets={widgets}
            disabled={disabled}
            readOnly={readOnly}
            locale={locale}
            forms={forms}
          />
        );

        const propertyWrapper = propertyConfig?.get('propertyWrapper');
        if (propertyWrapper && widgets?.[propertyWrapper]) {
          const PropertyWrapper = widgets[propertyWrapper] as ComponentType<SchemaFieldProps>;
          return (
            <PropertyWrapper key={name} {...props}>
              {field}
            </PropertyWrapper>
          );
        }

        return field;
      })
    : [];

  const Wrapper = getObjectWrapper(props);
  const PropertiesWrapper = getPropertiesWrapper(props);

  const wrappedProperties = PropertiesWrapper ? <PropertiesWrapper {...props}>{fields}</PropertiesWrapper> : fields;

  return Wrapper ? (
    <Wrapper {...props}>
      {discriminatorField}
      {wrappedProperties}
    </Wrapper>
  ) : (
    <>
      {discriminatorField}
      {wrappedProperties}
    </>
  );
}
